import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LocaleService {
  private readonly STORAGE_KEY = 'user_language';
  private localeSubject = new BehaviorSubject<string>('en-US');

  constructor(
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Initialize the language on app start
   */
  initializeLanguage(): void {
    const storedLang = localStorage.getItem(this.STORAGE_KEY) || 'en';
    this.changeLanguage(storedLang, false); // Set language without saving again
  }

  /**
   * Apply a language. On app start (saveToStorage = false) it is applied in place; a user's
   * choice is stored and the page reloaded, because LOCALE_ID, date formats and texts that
   * components already copied cannot be switched in a running app.
   */
  changeLanguage(lang: string, saveToStorage = true): void {
    if (!saveToStorage) {
      this.translate.use(lang);
      this.localeSubject.next(this.getLocaleFromLanguage(lang));
      this.applyTextDirection(lang);
      return;
    }
    if (!/^[a-z]{2,3}(-[A-Za-z0-9]{2,4})?$/.test(lang) || lang === (localStorage.getItem(this.STORAGE_KEY) || 'en')) {
      return;
    }
    localStorage.setItem(this.STORAGE_KEY, lang);
    this.document.defaultView?.location.reload();
  }

  private applyTextDirection(lang: string): void {
    const direction = this.getTextDirection(lang);
    this.document.documentElement.setAttribute('dir', direction);
    this.document.documentElement.setAttribute('lang', lang);
    this.document.body?.classList.toggle('rtl', direction === 'rtl');
  }

  /**
   * Convert language code to LOCALE_ID format
   */
  getLocaleFromLanguage(lang: string): string {
    switch (lang.split('-')[0]) {
      case 'de':
        return 'de-DE';
      case 'es':
        return 'es-ES';
      case 'fr':
        return 'fr-FR';
      case 'it':
        return 'it-IT';
      case 'nl':
        return 'nl-NL';
      case 'hu':
        return 'hu-HU';
      case 'sv':
        return 'sv-SE';
      case 'cs':
        return 'cs-CZ';
      case 'sk':
        return 'sk-SK';
      case 'hr':
        return 'hr-HR';
      case 'sl':
        return 'sl-SI';
      case 'bs':
        return 'bs-BA';
      case 'fi':
        return 'fi-FI';
      case 'ru':
        return 'ru-RU';
      case 'ja':
        return 'ja-JP';
      case 'tr':
        return 'tr-TR';
      case 'zh':
        return 'zh-CN';
      case 'ro':
        return 'ro-RO';
      case 'no':
        return 'nb-NO';
      case 'he':
        return 'he-IL';
      case 'ar':
        return 'ar-SA';
      case 'ko':
        return 'ko-KR';
      case 'pl':
        return 'pl-PL';
      case 'bg':
        return 'bg-BG';
      default:
        return 'en-US';
    }
  }

  /**
   * Check if language is RTL (Right-to-Left)
   */
  isRTL(lang: string): boolean {
    const rtlLanguages = ['he', 'ar', 'fa', 'ur'];
    return rtlLanguages.includes(lang.split('-')[0]);
  }

  /**
   * Get text direction for language
   */
  getTextDirection(lang: string): 'ltr' | 'rtl' {
    return this.isRTL(lang) ? 'rtl' : 'ltr';
  }

  /**
   * Expose the current locale as an observable
   */
  get locale$() {
    return this.localeSubject.asObservable();
  }
}
